if (!customElements.get("quick-add-modal")) {
  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent =
          this.querySelector("#QuickAddInfo") ||
          this.querySelector('[id^="QuickAddInfo-"]');
        this.footerSlot = this.querySelector("#QuickAddFooter");
      }

      hide(preventFocus = false) {
        if (this._abortController) {
          this._abortController.abort();
          this._abortController = null;
        }
        this._loading = false;
        this.modalContent.innerHTML = "";
        if (this.footerSlot) this.footerSlot.innerHTML = "";
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (this._loading) return;
        this._loading = true;

        // Abort any previous fetch
        if (this._abortController) this._abortController.abort();
        this._abortController = new AbortController();
        const signal = this._abortController.signal;

        // Clear previous content — loader shows via CSS :empty
        this.modalContent.innerHTML = "";

        // Open drawer immediately
        super.show(opener);

        fetch(opener.getAttribute("data-product-url"), { signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (signal.aborted) return;

            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]',
            );
            if (!this.productElement) return;

            this.productElement.classList.forEach((classApplied) => {
              if (
                classApplied.startsWith("color-") ||
                classApplied === "gradient"
              )
                this.modalContent.classList.add(classApplied);
            });

            this.preventDuplicatedIDs();
            this.removeDOMElements();

            // Always clear footer before injecting
    
            // Inject content — CSS hides loader automatically via :empty
            this.modalContent.innerHTML = this.productElement.innerHTML;

            this.runSkioInQuickAdd(this.modalContent);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            this.reorganizeForDrawer();
            this.syncCarouselActiveState();
          })
          .catch((e) => {
            if (e.name !== "AbortError") console.error(e);
          })
          .finally(() => {
            this._loading = false;
          });
      }

      /* ── Layout: reorder DOM for drawer view ── */

      reorganizeForDrawer() {
        const container = this.modalContent;
        const productGrid = container.querySelector(
          ".main-product-section-container, .product",
        );
        if (!productGrid) return;

        const mediaWrapper = productGrid.querySelector(
          ".product__media-wrapper",
        );
        const infoWrapper = productGrid.querySelector(
          ".product__info-wrapper",
        );
        if (!mediaWrapper || !infoWrapper) return;

        // Remove duplicate h1 titles, keep only the h2 link
        container
          .querySelectorAll("div.product__title h1")
          .forEach((el) => el.remove());

        // Keep only the first title block, remove duplicates
        const allTitleBlocks = container.querySelectorAll(
          ".c-product__headline",
        );
        for (let i = 1; i < allTitleBlocks.length; i++) {
          allTitleBlocks[i].remove();
        }

        // Force order: title first, then media, then info
        if (allTitleBlocks[0]) {
          productGrid.prepend(allTitleBlocks[0]);
        }
        if (mediaWrapper) {
          allTitleBlocks[0]
            ? allTitleBlocks[0].after(mediaWrapper)
            : productGrid.prepend(mediaWrapper);
        }

        // Normalize the gallery into a consistent structure
        this.normalizeGallery(mediaWrapper);

        // Move badges into the media wrapper so they overlay the featured image
        const badges = container.querySelector(".custom-badges-wrapper, .custom-badges-container");
        if (badges) {
          const sliderComponent = mediaWrapper.querySelector("slider-component") || mediaWrapper;
          sliderComponent.appendChild(badges);
        }

        // Move ATC to the sticky footer slot
        this.moveATCToFooter(container);

        // FORCE title above gallery — move it right before .qa-gallery or the first slider
        const titleBlock = container.querySelector(".c-product__headline");
        const gallery = container.querySelector(".qa-gallery") ||
                        container.querySelector("slider-component") ||
                        container.querySelector(".product__media-list");
        if (titleBlock && gallery && gallery.parentNode) {
          gallery.parentNode.insertBefore(titleBlock, gallery);
        }

        // Refresh Yotpo reviews widget
        if (typeof yotpo !== "undefined") {
          try {
            yotpo.refreshWidgets();
          } catch (e) {}
        }

        container.scrollTop = 0;
      }

      normalizeGallery(mediaWrapper) {
        // Find the main slider list — not thumbnails
        const mainList =
          mediaWrapper.querySelector('#Slider-main-media-slide') ||
          mediaWrapper.querySelector('[id^="Slider-Gallery-"]') ||
          mediaWrapper.querySelector('.product__media-list');

        // Collect images only from the main slider
        const images = [];
        const seen = new Set();
        const sourceItems = mainList
          ? mainList.querySelectorAll(':scope > .product__media-item, :scope > li')
          : mediaWrapper.querySelectorAll('.product__media-item');

        sourceItems.forEach((item) => {
          const img = item.querySelector('img');
          if (!img) return;
          // Deduplicate by src
          const key = img.src.split('?')[0];
          if (seen.has(key)) return;
          seen.add(key);
          images.push({
            src: img.src,
            srcset: img.getAttribute('srcset') || '',
            alt: img.alt || '',
            width: img.width,
            height: img.height,
          });
        });

        if (!images.length) return;

        // Build a uniform gallery
        const gallery = document.createElement('div');
        gallery.className = 'qa-gallery';

        // Main slider
        const sliderWrap = document.createElement('slider-component');
        sliderWrap.className = 'qa-gallery__viewer';
        const sliderList = document.createElement('ul');
        sliderList.id = 'Slider-main-media-slide';
        sliderList.className = 'qa-gallery__list';

        images.forEach((imgData, i) => {
          const li = document.createElement('li');
          li.className = 'product__media-item qa-gallery__slide';
          li.dataset.index = String(i + 1);
          const imgEl = document.createElement('img');
          imgEl.src = imgData.src;
          if (imgData.srcset) imgEl.setAttribute('srcset', imgData.srcset);
          imgEl.alt = imgData.alt;
          imgEl.loading = i === 0 ? 'eager' : 'lazy';
          imgEl.setAttribute('sizes', '500px');
          li.appendChild(imgEl);
          sliderList.appendChild(li);
        });

        sliderWrap.appendChild(sliderList);
        gallery.appendChild(sliderWrap);

        // Thumbnails (only if more than 1 image)
        if (images.length > 1) {
          const thumbContainer = document.createElement('div');
          thumbContainer.className = 'carousel-thumb-container';

          images.forEach((imgData, i) => {
            const thumb = document.createElement('div');
            thumb.className = 'product__media-item qa-gallery__thumb' + (i === 0 ? ' active' : '');
            thumb.dataset.index = String(i + 1);
            const thumbImg = document.createElement('img');
            thumbImg.src = imgData.src;
            thumbImg.alt = imgData.alt;
            thumbImg.loading = 'lazy';
            thumbImg.setAttribute('sizes', '60px');
            thumb.appendChild(thumbImg);
            thumbContainer.appendChild(thumb);
          });

          gallery.appendChild(thumbContainer);
        }

        // Replace the original gallery content
        mediaWrapper.innerHTML = '';
        mediaWrapper.appendChild(gallery);
      }

      moveATCToFooter(container) {
        // Hide all original ATC sections
        container.querySelectorAll(".c-product-action").forEach((el) => {
          el.style.display = "none";
        });

        // Find the source form to sync values from
        const sourceForm = container.querySelector('product-form form, form[action*="/cart/add"]');
        if (!sourceForm) return;

        // Get the original button's icon and spinner HTML
        const originalBtn = container.querySelector('.product-form__submit, [type="submit"], button[name="add"]');
        const iconEl = originalBtn?.querySelector('.icon, svg:not(.spinner)');
        const iconHTML = iconEl ? iconEl.outerHTML : '';
        const spinnerEl = originalBtn?.querySelector('.loading__spinner') ||
          document.querySelector('#QuickAddLoader');
        const spinnerHTML = spinnerEl ? `<span class="loading__spinner hidden">${spinnerEl.innerHTML}</span>` : '';

        // Inject into the fixed footer slot (outside the scroll area)
        const footerEl = this.footerSlot;
        if (!footerEl) return;
        footerEl.innerHTML = `
          <product-form class="product-form" data-hide-errors="true">
            <form method="post" action="/cart/add" accept-charset="UTF-8" enctype="multipart/form-data">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="">
              <input type="hidden" name="selling_plan" value="">
              <input type="hidden" name="quantity" value="1">
              <div class="product-form__buttons">
                <button type="submit" name="add" class="product-form__submit">
                  <span class="qa-atc-label">${iconHTML} ADD TO CART</span>
                  ${spinnerHTML}
                </button>
              </div>
            </form>
          </product-form>
        `;

        // References
        const footerForm = footerEl.querySelector("form");
        const footerIdInput = footerForm.querySelector('input[name="id"]');
        const footerPlanInput = footerForm.querySelector('input[name="selling_plan"]');
        const footerQtyInput = footerForm.querySelector('input[name="quantity"]');
        const footerLabel = footerEl.querySelector(".qa-atc-label");

        // Sync values from source form to footer form
        const syncFormValues = () => {
          const id = sourceForm.querySelector('input[name="id"]');
          const plan = sourceForm.querySelector('input[name="selling_plan"]');
          const qty = sourceForm.querySelector('input[name="quantity"]');
          if (id) footerIdInput.value = id.value;
          if (plan) footerPlanInput.value = plan.value;
          if (qty) footerQtyInput.value = qty.value;
        };

        // Price label
        const getPrice = () => {
          const sel = container.querySelector(".skio-group-container--selected");
          if (sel) {
            const p = sel.querySelector("[skio-subscription-price], .colored-discount, [skio-onetime-price]");
            if (p) return p.textContent.trim();
          }
          const p = container.querySelector(".price--on-sale .price-item--sale, .price .price-item--regular");
          return p ? p.textContent.trim() : "";
        };

        const updateLabel = () => {
          if (!footerLabel) return;
          syncFormValues();
          const price = getPrice();
          footerLabel.innerHTML = iconHTML + " ADD TO CART" + (price ? " - " + price : "");
        };

        // Initial sync
        syncFormValues();
        updateLabel();

        // Sync on any change in the source form
        new MutationObserver(syncFormValues).observe(sourceForm, {
          attributes: true, childList: true, subtree: true
        });

        // Price update listeners
        const up = () => requestAnimationFrame(updateLabel);
        container.querySelectorAll('.skio-group-input, .skio-frequency, variant-selects select, variant-radios input[type="radio"], .main-product-options-container input[type="radio"]').forEach((el) => el.addEventListener("change", up));
        container.querySelectorAll('[skio-subscription-price], [skio-onetime-price], .colored-discount').forEach((el) => new MutationObserver(up).observe(el, { childList: true, characterData: true, subtree: true }));
      }

      /* ── Carousel: dots, thumbnails, single-slide viewport ── */

      syncCarouselActiveState() {
        const sliders = Array.from(
          this.modalContent.querySelectorAll("#Slider-main-media-slide"),
        );
        if (!sliders.length) return;

        const isVisible = (el) =>
          !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

        sliders.forEach((mainSlider) => {
          const getSlides = () =>
            Array.from(
              mainSlider.querySelectorAll("li.product__media-item"),
            );
          const thumbnailItems = Array.from(
            this.modalContent.querySelectorAll(
              ".carousel-thumb-container .product__media-item[data-index]",
            ),
          );
          const getSlideWidth = () => {
            const firstSlide = getSlides()[0];
            return firstSlide?.clientWidth || mainSlider.clientWidth || 1;
          };

          // Strip PDP grid classes so CSS can handle the single-slide layout
          mainSlider.classList.remove(
            "grid",
            "grid--peek",
            "grid--1-col-tablet-down",
            "grid--2-col-tablet",
            "grid--3-col-tablet",
            "grid--4-col-desktop",
          );

          // Dots
          let dots =
            mainSlider.parentElement?.querySelector(".qa-slider-dots");
          if (dots) dots.remove();
          dots = document.createElement("div");
          dots.className = "qa-slider-dots";

          const slides = getSlides();
          slides.forEach((_, index) => {
            const dot = document.createElement("button");
            dot.type = "button";
            dot.className = "qa-slider-dot";
            dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
            dot.addEventListener("click", () => {
              mainSlider.scrollTo({
                left: getSlideWidth() * index,
                behavior: "smooth",
              });
            });
            dots.appendChild(dot);
          });

          const sliderHost =
            mainSlider.closest("slider-component") ||
            mainSlider.parentElement;
          sliderHost?.appendChild(dots);

          const dotButtons = Array.from(
            dots.querySelectorAll(".qa-slider-dot"),
          );

          const setActiveThumbnail = (activeIndex) => {
            if (!thumbnailItems.length) return;
            thumbnailItems.forEach((thumb) =>
              thumb.classList.remove("active", "is-active"),
            );
            const activeThumb = thumbnailItems.find(
              (thumb) =>
                Number.parseInt(thumb.dataset.index, 10) === activeIndex,
            );
            activeThumb?.classList.add("active");
          };

          const setActive = () => {
            if (!isVisible(mainSlider)) return;
            const currentSlides = getSlides();
            if (!currentSlides.length) return;

            const slideWidth = currentSlides[0].clientWidth || 1;
            const activeIndex =
              Math.round(mainSlider.scrollLeft / slideWidth) + 1;
            const boundedIndex = Math.min(
              Math.max(activeIndex, 1),
              currentSlides.length,
            );

            currentSlides.forEach((slide) =>
              slide.classList.remove("active", "is-active"),
            );
            currentSlides[boundedIndex - 1]?.classList.add(
              "active",
              "is-active",
            );
            setActiveThumbnail(boundedIndex);
            dotButtons.forEach((dot, index) => {
              dot.classList.toggle("is-active", index + 1 === boundedIndex);
            });
          };

          mainSlider.addEventListener(
            "scroll",
            () => window.requestAnimationFrame(setActive),
            { passive: true },
          );

          thumbnailItems.forEach((thumb) => {
            thumb.addEventListener("click", (event) => {
              event.preventDefault();
              const thumbIndex = Number.parseInt(thumb.dataset.index, 10);
              if (!thumbIndex || Number.isNaN(thumbIndex)) return;
              mainSlider.scrollTo({
                left: getSlideWidth() * (thumbIndex - 1),
                behavior: "smooth",
              });
              setTimeout(setActive, 0);
            });
          });

          window.requestAnimationFrame(setActive);
        });
      }

      /* ── Helpers ── */

      setInnerHTML(element, html) {
        element.innerHTML = html;
      }

      runSkioInQuickAdd(container) {
        container
          .querySelectorAll('script[type="lazyJs"]')
          .forEach((oldScript) => {
            const newScript = document.createElement("script");
            if (oldScript.src) newScript.src = oldScript.src;
            newScript.type = "text/javascript";
            newScript.text = oldScript.textContent;
            oldScript.replaceWith(newScript);
          });
        if (window.SkioPlanPickerAutoInit) {
          window.SkioPlanPickerAutoInit();
        }
      }

      preventVariantURLSwitching() {
        this.modalContent
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((picker) => {
            picker.setAttribute("data-update-url", "false");
            picker.dataset.updateUrl = "false";
          });
      }

      removeDOMElements() {
        const el = this.productElement;
        el.querySelector("pickup-availability")?.remove();
        el.querySelector("product-modal")?.remove();
        el.querySelectorAll("modal-dialog").forEach((m) => m.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`,
          );
        this.productElement
          .querySelectorAll("variant-selects, product-info")
          .forEach((element) => {
            element.dataset.originalSection = sectionId;
          });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector(
          '[id^="Slider-Gallery"]',
        );
        if (!galleryList) return;
        galleryList.setAttribute("role", "presentation");
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute("role", "presentation"));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector(".product");
        if (!product || !product.classList.contains("product--columns")) return;

        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        let mediaImageSizes =
          "(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)";
        if (product.classList.contains("product--medium")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "605px");
        } else if (product.classList.contains("product--small")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "495px");
        }
        mediaImages.forEach((img) =>
          img.setAttribute("sizes", mediaImageSizes),
        );
      }
    },
  );
}
